import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../../../../lib/network/api";

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export type ProjectCategory =
  | "AI Training"
  | "Data Annotation"
  | "Code Review"
  | "Content Moderation"
  | "Audio Transcription"
  | "Sentiment Analysis"
  | "Prompt Engineering"
  | "Image Classification"
  | "Text Review"
  | "Video Annotation";

export type ProjectDifficulty = "Easy" | "Medium" | "Hard";

export type UserProjectStatus =
  | "assigned"
  | "pending"
  | "approved"
  | "rejected"
  | "active"
  | "completed"
  | "terminated";

export interface ProjectSkill {
  name: string;
  required: boolean;
}

export interface ProjectResponsibility {
  order: number;
  description: string;
}

export interface Project {
  _id: string;
  projectId: string;
  title: string;
  category: ProjectCategory;
  description: string;
  hourlyRate: number;
  estimatedHours: string;
  difficulty: ProjectDifficulty;
  totalSlots?: number;
  availableSlots?: number;
  responsibilities: ProjectResponsibility[];
  skillsRequired: ProjectSkill[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApplicationAnswers {
  interest: string;
  experience: string;
  availability: number;
}

export interface UserProject {
  _id: string;
  userId?: string;
  projectId: string;
  project: Project;
  verificationLink: string;
  status: UserProjectStatus;
  documentVerified: boolean;
  assignedAt: string;
  assignedBy?: string;
  assignmentNote?: string;
  applicationAnswers?: ApplicationAnswers;
  appliedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  startedAt?: string;
  completedAt?: string;
  terminatedAt?: string;
  terminationReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserProjectsData {
  available: UserProject[];
  pending: UserProject[];
  myProjects: UserProject[];
  counts: {
    available: number;
    pending: number;
    myProjects: number;
  };
}

export interface UserProjectsResponse {
  message: string;
  statusCode: number;
  data: UserProjectsData;
}

export interface UserProjectDetailsResponse {
  message: string;
  statusCode: number;
  data: UserProject;
}

export interface ApplyForProjectPayload {
  availability: string; // Changed from number to string to match "5-10", "10-20", etc.
  acceptedTerms: boolean;
  acceptedGuidelines: boolean;
}

export interface ApplyForProjectResponse {
  message: string;
  statusCode: number;
  data: {
    userProject: UserProject;
    verificationLink: string;
  };
}

export interface StartProjectResponse {
  message: string;
  statusCode: number;
  data: UserProject;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

// Get user's projects (available, pending, my projects)
export const getUserProjects = async (
  userId: string
): Promise<UserProjectsData> => {
  const response = await api.get<UserProjectsResponse>(
    `/projects/user/${userId}`
  );

  // Handle both cases: response.data might be the full response or already unwrapped
  const responseData = response.data as any;

  // If response.data has a 'data' property, it's the full response
  if (responseData.data && responseData.data.available !== undefined) {
    return responseData.data;
  }

  // If response.data already has 'available', it's already unwrapped
  if (responseData.available !== undefined) {
    return responseData;
  }

  // Fallback - return as is
  return responseData;
};

// Get single project details for user
export const getUserProjectDetails = async (
  userId: string,
  userProjectId: string
): Promise<UserProject> => {
  const response = await api.get<UserProjectDetailsResponse>(
    `/projects/user/${userId}/${userProjectId}`
  );

  const responseData = response.data as any;

  // If response.data has a nested 'data' property with '_id', it's the full response
  if (responseData.data && responseData.data._id) {
    return responseData.data;
  }

  // If response.data already has '_id', it's already unwrapped
  if (responseData._id) {
    return responseData;
  }

  return responseData;
};

// Apply for a project
export const applyForProject = async (
  userId: string,
  userProjectId: string,
  payload: ApplyForProjectPayload
): Promise<ApplyForProjectResponse> => {
  const response = await api.post<ApplyForProjectResponse>(
    `/projects/user/${userId}/${userProjectId}/apply`,
    payload
  );
  return response.data as any;
};

// Start working on a project (after approval)
export const startProject = async (
  userId: string,
  userProjectId: string
): Promise<StartProjectResponse> => {
  const response = await api.post<StartProjectResponse>(
    `/projects/user/${userId}/${userProjectId}/start`,
    {}
  );
  return response.data as any;
};

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

// Hook to fetch user's projects
export const useGetUserProjects = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["userProjects", userId],
    queryFn: () => getUserProjects(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

// Hook to fetch single project details
export const useGetUserProjectDetails = (
  userId: string | undefined,
  userProjectId: string | undefined
) => {
  return useQuery({
    queryKey: ["userProjectDetails", userId, userProjectId],
    queryFn: () => getUserProjectDetails(userId!, userProjectId!),
    enabled: !!userId && !!userProjectId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

// Hook to apply for a project
export const useApplyForProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      userProjectId,
      payload,
    }: {
      userId: string;
      userProjectId: string;
      payload: ApplyForProjectPayload;
    }) => applyForProject(userId, userProjectId, payload),
    onSuccess: (response, variables) => {
      toast.success(response.message || "Application submitted successfully!");
      queryClient.invalidateQueries({
        queryKey: ["userProjects", variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "userProjectDetails",
          variables.userId,
          variables.userProjectId,
        ],
      });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to submit application"
      );
    },
  });
};

// Hook to start a project
export const useStartProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      userProjectId,
    }: {
      userId: string;
      userProjectId: string;
    }) => startProject(userId, userProjectId),
    onSuccess: (response, variables) => {
      toast.success(response.message || "Project started successfully!");
      queryClient.invalidateQueries({
        queryKey: ["userProjects", variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "userProjectDetails",
          variables.userId,
          variables.userProjectId,
        ],
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to start project");
    },
  });
};
